import React from 'react';
import moment from 'moment';
import { Avatar, Tooltip } from 'antd';
import uuid from 'uuid';
import { useHistory, useLocation } from 'react-router-dom';
import cnnaid from '../../../../../../../../assets/icons/transactions/cnnaid.png';
import cnnaip from '../../../../../../../../assets/icons/transactions/cnnaip.png';
import cnnafingerprint from '../../../../../../../../assets/icons/transactions/cnnafingerprint.png';
import { ReactComponent as GlobePhoneIcon } from '../../../../../../../../assets/icons/transactions/globephone.svg';
import ImpressionTimeline from './ImpressionsTimeLine';

// Global Styling
import {
  TableColumnTitle,
  PrimaryTableRowText,
} from '../../../../../../../shared/globalStyling/styledText';
import { formatCurrency2SigFig } from '../../../../../../../../core/utils/campaigns';
import SimpleTable from '../../../../../../../shared/SimpleTable';

const TransactionsTable = ({
  transactionData = [],
  transactionsPagination = { total: 0, hasNextPage: false },
  getImpressions,
}) => {
  const history = useHistory();
  const location = useLocation();

  // Read pagination from URL params
  const queryParams = new URLSearchParams(location.search);
  const currentPage = parseInt(queryParams.get('page')) || 1;
  const pageSize = parseInt(queryParams.get('pageSize')) || 10;

  const handlePaginationChange = (page, newPageSize) => {
    const params = new URLSearchParams(location.search);

    if (newPageSize !== pageSize) {
      // Page size changed - reset to page 1
      params.set('page', '1');
      params.set('pageSize', newPageSize.toString());
    } else {
      // Just page changed
      params.set('page', page.toString());
      params.set('pageSize', pageSize.toString());
    }

    history.push({ search: params.toString() });
  };

  const columns = [
    {
      title: <TableColumnTitle text={'Order'} />,
      dataIndex: 'orderId',
      key: 'orderId',
      align: 'left',
      width: '13%',
      sorter: (a, b) => (a.orderId || '').localeCompare(b.orderId || ''),
      render: (orderId) => <PrimaryTableRowText text={orderId || 'N/A'} />,
    },
    {
      title: <TableColumnTitle text={'Last Impression'} />,
      dataIndex: 'lastImpression',
      key: 'lastImpression',
      align: 'left',
      width: '20%',
      sorter: (a, b) => {
        if (!a.lastImpression && !b.lastImpression) return 0;
        if (!a.lastImpression) return 1;
        if (!b.lastImpression) return -1;

        const dateA = moment(a.lastImpression);
        const dateB = moment(b.lastImpression);

        return dateA.valueOf() - dateB.valueOf();
      },
      render: (lastImpression) => (
        <PrimaryTableRowText
          text={lastImpression ? moment(lastImpression).format('MM/DD/YY HH:mm') : ''}
        />
      ),
    },
    {
      title: <TableColumnTitle text={'Impressions'} />,
      dataIndex: 'totalImpressions',
      key: 'impressions',
      align: 'left',
      width: '17%',
      sorter: (a, b) => (parseInt(a.totalImpressions) || 0) - (parseInt(b.totalImpressions) || 0),
      render: (totalImpressions) => <PrimaryTableRowText text={totalImpressions || 0} />,
    },
    {
      title: <TableColumnTitle text={'Ordered on'} />,
      dataIndex: 'transactionDate',
      key: 'transactionDate',
      align: 'left',
      width: '15%',
      sorter: (a, b) => {
        if (!a.transactionDate && !b.transactionDate) return 0;
        if (!a.transactionDate) return 1;
        if (!b.transactionDate) return -1;
        return moment(a.transactionDate).unix() - moment(b.transactionDate).unix();
      },
      render: (transactionDate) => (
        <PrimaryTableRowText
          text={transactionDate ? moment(transactionDate).format('MM/DD/YY HH:mm') : ''}
        />
      ),
    },
    {
      title: <TableColumnTitle text={'Total'} />,
      dataIndex: 'revenue',
      key: 'revenue',
      align: 'left',
      width: '12%',
      sorter: (a, b) => (a.revenue || 0) - (b.revenue || 0),
      render: (revenue) => (
        <PrimaryTableRowText
          text={
            revenue && !isNaN(revenue) ? formatCurrency2SigFig(revenue) : formatCurrency2SigFig(0)
          }
        />
      ),
    },
    {
      title: <TableColumnTitle text={''} />,
      dataIndex: 'attrMethod',
      key: 'attrMethod',
      align: 'left',
      width: '8%',
      render: (attrMethod) => {
        let attributionIcon = cnnaip;
        let tip = '';

        if (attrMethod === 'COOKIE') {
          attributionIcon = cnnaid;
          tip = 'Cookie Match - Deterministic match using browser based cookie / uuid match.';
        } else if (attrMethod === 'FINGERPRINT') {
          attributionIcon = cnnafingerprint;
          tip =
            'User Match - Probabilistic match using user fingerprint. (User fingerprint - The combination of user agent and IP address to produce a unique hash)';
        } else if (attrMethod === 'DEVICE_FINGERPRINT') {
          tip =
            'Device Match - Probabilistic match using user device model information (Device model, OS, OS version ) combined with IP address';
        } else if (attrMethod === 'IP') {
          tip = 'IP Match - Probabilistic match using IP address.';
          attributionIcon = cnnaip;
        }

        const attributionIcon_element = () => {
          if (attrMethod === 'DEVICE_FINGERPRINT') {
            return (
              <span role="img" className="anticon" style={{ verticalAlign: '-5px' }}>
                <GlobePhoneIcon
                  height="35"
                  width="35"
                  style={{
                    opacity: '1.0',
                  }}
                />
              </span>
            );
          } else {
            return <Avatar src={attributionIcon} />;
          }
        };

        return <Tooltip title={tip}>{attributionIcon_element()}</Tooltip>;
      },
    },
  ];

  return (
    <SimpleTable
      footer={transactionData}
      bordered={false}
      columns={columns}
      data={transactionData.map((transaction) => ({
        ...transaction,
        key: `${transaction.id}${uuid.v4()}`,
      }))}
      scroll={{ x: 850, y: 400 }}
      onChange={(paginationObject, filtersObject, sorterObject) => {
        handlePaginationChange(paginationObject.current, paginationObject.pageSize);
      }}
      pagination={{
        pageSize: pageSize,
        total: transactionsPagination.total,
        current: currentPage,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50', '100'],
      }}
      expandable={{
        expandedRowRender: (record) => {
          return <ImpressionTimeline transactionId={record.id} getImpressions={getImpressions} />;
        },
        rowExpandable: (record) => record.totalImpressions > 0,
      }}
      rowClassName={(record) => {
        return record && record.totalImpressions > 0 ? '' : 'hide-expand';
      }}
    />
  );
};

export default TransactionsTable;
